import { Link } from "react-router";
import { Alert } from "../components/ui";
import {
  day,
  daysUntil,
  Kpi,
  Loadable,
  moneyMap,
  PageHead,
  RestaurantLink,
  type Row,
  Section,
  Table,
  usd,
  useAdmin,
} from "./ui";

// Panou: the business at a glance — money, the trial funnel, activation, AI cost and what needs attention today.

interface Business {
  subscriptions: {
    trialing: number;
    active: number;
    pastDue: number;
    canceled: number;
    expired: number;
    free: number;
    trialEndedUnpaid: number;
  };
  revenue: { month: Record<string, number>; last30: Record<string, number>; mrr: Record<string, number> };
  restaurants: { total: number; active: number; suspended: number; new7: number; new30: number };
  users: { total: number; new30: number };
  activation: { published: number; views30: number };
  trialsEndingSoon: Row[];
  ai: { provider: string; monthMicro: number; last30Micro: number; calls30: number; top: Row[] };
  attention: {
    supportOpen: number;
    fiscalFailed: number;
    pastDue: number;
    stuckImports: number;
    chatUnread: number;
  };
  email: { month: string; sent: number; cap: number };
  registration: string;
}

const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : "—");

export function Dashboard() {
  const q = useAdmin<Business>(["business"], "/business");
  return (
    <Loadable q={q}>
      {(d) => {
        const s = d.subscriptions;
        const decided = s.active + s.pastDue + s.canceled + s.expired + s.trialEndedUnpaid;
        const todo = [
          d.attention.chatUnread > 0 && {
            to: "/admin/chat",
            text: `${d.attention.chatUnread} ${d.attention.chatUnread === 1 ? "restaurant așteaptă" : "restaurante așteaptă"} răspuns în chat`,
          },
          d.attention.supportOpen > 0 && {
            to: "/admin/suport",
            text: `${d.attention.supportOpen} mesaje de suport fără răspuns`,
          },
          d.attention.fiscalFailed > 0 && {
            to: "/admin/abonamente?fiscal=failed",
            text: `${d.attention.fiscalFailed} facturi fiscale refuzate de SmartBill`,
          },
          d.attention.pastDue > 0 && {
            to: "/admin/restaurante?sub=past_due",
            text: `${d.attention.pastDue} restaurante cu plată restantă`,
          },
          d.attention.stuckImports > 0 && {
            to: "/admin/sistem",
            text: `${d.attention.stuckImports} importuri blocate de peste 30 de minute`,
          },
          s.trialEndedUnpaid > 0 && {
            to: "/admin/restaurante?sub=trialing",
            text: `${s.trialEndedUnpaid} restaurante au terminat proba fără să plătească`,
          },
        ].filter(Boolean) as { to: string; text: string }[];
        return (
          <>
            <PageHead
              title="Panou"
              sub={`Înregistrare ${d.registration === "invite" ? "doar prin invitație" : "deschisă"} · AI: ${d.ai.provider}`}
            />
            {todo.length > 0 ? (
              <Alert tone="warning" title="De rezolvat">
                <ul className="admin-todo">
                  {todo.map((t) => (
                    <li key={t.to + t.text}>
                      <Link to={t.to}>{t.text}</Link>
                    </li>
                  ))}
                </ul>
              </Alert>
            ) : (
              <Alert tone="success">Nimic urgent: niciun mesaj, factură refuzată sau plată restantă.</Alert>
            )}

            <Section title="Bani">
              <div className="kpi-grid">
                <Kpi label="MRR estimat (fără TVA)" value={moneyMap(d.revenue.mrr)} hint="anual = /12" />
                <Kpi label="Încasat luna aceasta" value={moneyMap(d.revenue.month)} hint="cu TVA" />
                <Kpi label="Încasat în ultimele 30 de zile" value={moneyMap(d.revenue.last30)} />
                <Kpi label="Clienți plătitori" value={s.active} to="/admin/restaurante?sub=active" />
                <Kpi
                  label="Plată restantă"
                  value={s.pastDue}
                  to="/admin/restaurante?sub=past_due"
                  tone={s.pastDue ? "warning" : undefined}
                />
              </div>
            </Section>

            <Section title="Probă gratuită → plată">
              <div className="kpi-grid">
                <Kpi label="În probă acum" value={s.trialing} to="/admin/restaurante?sub=trialing" />
                <Kpi
                  label="Probă încheiată, neplătit"
                  value={s.trialEndedUnpaid}
                  tone={s.trialEndedUnpaid ? "warning" : undefined}
                />
                <Kpi
                  label="Conversie"
                  value={pct(s.active + s.pastDue, decided)}
                  hint="plătitori / probe încheiate"
                />
                <Kpi label="Anulate" value={s.canceled} to="/admin/restaurante?sub=canceled" />
                <Kpi label="Acces gratuit (test)" value={s.free} to="/admin/restaurante?sub=beta" />
              </div>
              <h3 className="h3" style={{ margin: 0 }}>
                Probe care se încheie în 7 zile
              </h3>
              <Table
                head={["Restaurant", "Se încheie", "Zile rămase"]}
                empty="Nicio probă nu se încheie în următoarele 7 zile."
                rows={d.trialsEndingSoon.map((r) => [
                  <RestaurantLink key="r" id={r.id} name={r.name} />,
                  day(r.trial_ends_at),
                  String(daysUntil(r.trial_ends_at)),
                ])}
              />
            </Section>

            <Section title="Restaurante și folosire">
              <div className="kpi-grid">
                <Kpi label="Restaurante" value={d.restaurants.total} to="/admin/restaurante" />
                <Kpi label="Noi (7 / 30 zile)" value={`${d.restaurants.new7} / ${d.restaurants.new30}`} />
                <Kpi
                  label="Cu meniu publicat"
                  value={d.activation.published}
                  hint={`${pct(d.activation.published, d.restaurants.total)} din restaurante`}
                />
                <Kpi
                  label="Deschideri meniu (30 zile)"
                  value={d.activation.views30.toLocaleString("ro-RO")}
                />
                <Kpi
                  label="Suspendate"
                  value={d.restaurants.suspended}
                  to="/admin/restaurante?sub=suspended"
                />
              </div>
            </Section>

            <Section title="AI și e-mail" actions={<Link to="/admin/ai">Detalii AI</Link>}>
              <div className="kpi-grid">
                <Kpi label="Cost AI luna aceasta" value={usd(d.ai.monthMicro)} />
                <Kpi label="Cost AI 30 zile" value={usd(d.ai.last30Micro)} hint={`${d.ai.calls30} apeluri`} />
                <Kpi
                  label={`E-mailuri trimise (${d.email.month})`}
                  value={`${d.email.sent} / ${d.email.cap}`}
                  tone={d.email.sent > d.email.cap * 0.8 ? "warning" : undefined}
                />
              </div>
              <h3 className="h3" style={{ margin: 0 }}>
                Cel mai mare consum AI (din plafonul de 2 $ pe restaurant)
              </h3>
              <Table
                head={["Restaurant", "Consumat"]}
                rows={d.ai.top
                  .filter((r) => Number(r.used_micro) > 0)
                  .map((r) => [<RestaurantLink key="r" id={r.id} name={r.name} />, usd(r.used_micro)])}
                empty="Niciun consum AI încă."
              />
            </Section>
          </>
        );
      }}
    </Loadable>
  );
}
